import React from 'react';
import { OrderSummary } from '../../../types';
import { OrderStatusBadge, PaymentStatusBadge, DateDisplay } from '../atoms';
import './OrderCard.css';

interface OrderCardProps {
  order: OrderSummary;
  onClick?: (order: OrderSummary) => void;
  onEdit?: (order: OrderSummary) => void;
  className?: string;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, onClick, onEdit, className = '' }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(order);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(order);
    }
  };

  return (
    <div 
      className={`order-card ${className}`}
      onClick={handleClick}
    >
      <div className="order-card-header">
        <div className="order-number">
          <h3>{order.orderNumber}</h3>
          <DateDisplay date={order.createdAt} format="relative" />
        </div>
        <div className="order-statuses">
          <OrderStatusBadge status={order.status} />
          <PaymentStatusBadge status={order.paymentStatus} />
          {onEdit && (
            <button
              className="edit-order-btn"
              onClick={handleEdit}
              title="Edit order"
              aria-label="Edit order"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.334 2.00004C11.5091 1.82494 11.7169 1.68605 11.9457 1.59129C12.1745 1.49653 12.4197 1.44775 12.6673 1.44775C12.9149 1.44775 13.1601 1.49653 13.3889 1.59129C13.6177 1.68605 13.8256 1.82494 14.0007 2.00004C14.1758 2.17513 14.3147 2.383 14.4094 2.61178C14.5042 2.84055 14.553 3.08575 14.553 3.33337C14.553 3.58099 14.5042 3.82619 14.4094 4.05497C14.3147 4.28374 14.1758 4.49161 14.0007 4.66671L5.00065 13.6667L1.33398 14.6667L2.33398 11L11.334 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="order-card-body">
        <div className="customer-info">
          <p className="customer-name">{order.customerName}</p>
          <p className="customer-email">{order.customerEmail}</p>
        </div>
        
        {order.itemDetails && order.itemDetails.length > 0 && (
          <div className="order-items-list">
            {order.itemDetails.map((item, index) => (
              <div key={index} className="order-item-detail">
                <div className="item-header">
                  <span className="item-name">
                    {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                  </span>
                  <span className="item-price">€{item.totalPrice.toFixed(2)}</span>
                </div>
                <div className="item-specs">
                  {item.size && <span className="spec">Size: {item.size}</span>}
                  {item.shape && <span className="spec">Shape: {item.shape}</span>}
                  {item.layers && <span className="spec">Layers: {item.layers}</span>}
                  {item.flavors && item.flavors.length > 0 && (
                    <span className="spec">Flavors: {item.flavors.join(', ')}</span>
                  )}
                  {item.filling && <span className="spec">Filling: {item.filling}</span>}
                  {item.icing && <span className="spec">Icing: {item.icing}</span>}
                  {item.baseColor && <span className="spec">Base: {item.baseColor}</span>}
                  {item.pipingColor && <span className="spec">Piping: {item.pipingColor}</span>}
                  {item.addOns && item.addOns.length > 0 && (
                    <span className="spec">Add-ons: {item.addOns.map(a => a.split('+€')[0].trim()).join(', ')}</span>
                  )}
                  {item.matchingCupcakes && item.matchingCupcakes.length > 0 && (
                    <span className="spec">Cupcakes: {item.matchingCupcakes.map(c => c.split('+€')[0].trim()).join(', ')}</span>
                  )}
                  {item.hasImageOnCake && <span className="spec">Image on cake</span>}
                  {item.specialInstructions && (
                    <span className="spec message">Message: {item.specialInstructions}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="order-details">
          {/* <div className="order-item-count">
            <span className="label">Items:</span>
            <span className="value">{order.itemCount}</span>
          </div> */}
          <div className="order-total">
            <span className="label">Total:</span>
            <span className="value">€{order.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {(order.deliveryOption || order.deliveryDate) && (
        <div className="order-card-footer">
          {order.deliveryOption && (
            <div className="delivery-option">
              <span className={`delivery-option-badge ${order.deliveryOption}`}>
                {order.deliveryOption === 'delivery' ? 'Delivery' : 'Collection'}
              </span>
              {order.deliveryDate && (
              <div className="delivery-option-badge">
                  <DateDisplay date={order.deliveryDate} format="short" />
              </div>
              )}
            </div>
          )}
          
        </div>
      )}
    </div>
  );
};

export default OrderCard;
